import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MilkMoneyQuery } from '~/QueryHooks/MilkMoneyQuery';
import Layout from '~/components/Layout'
import notify from '~/components/Notify';
import i18n from '~/components/i18n';
import { useAuth } from '~/routes/components/AuthProvider';
import { FiltersWrapper } from '../../Settings/FTPLogs/style';
import { DateInput } from 'semantic-ui-calendar-react';
import { Dropdown } from 'semantic-ui-react';
import TableComponent from '~/components/TableComponent';
import { DownloadService } from '~/api/services/downloadFiles';
import { LIGHT_USER_ROLE_SLUG } from '~/Constants';
import ExcelExport from '~/components/ExcelExport';

const getTableColumnHeaders = () => [
  {
    name: i18n.t('Type'),
    key: 'typeLabel'
  },
  {
    name: i18n.t('Action'),
    key: 'slugLabel'
  },
  {
    name: i18n.t('Start'),
    key: 'start',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('End'),
    key: 'end',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    name: i18n.t('Exported'),
    key: 'exported'
  },
  {
    name: i18n.t('Imported'),
    key: 'imported'
  },
  {
    name: i18n.t('Status'),
    key: 'status',
    type: 'verified'
  },
  {
    name: i18n.t('Actions')
  }
];

const FtpLogs: React.FC = () => {
  const { t } = useTranslation()
  const { systemData: { languages, logged: { languageId, roleSlug } } } = useAuth()
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const languageLocal = languages.find(language => language.id === languageId)

  const [dataFiltered, setDataFiltered] = useState({
    rows: 10,
    page: 0,
    dateFrom: '',
    dateTo: '',
    type: '',
    ftp: ''
  })
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })

  const actions = [
    {
      popupMessage: i18n.t('Original files'),
      type: 'icon',
      name: 'file alternate',
      size: 'large',
      actionType: 'download original'
    },
    {
      popupMessage: i18n.t('Download log file'),
      type: 'icon',
      name: 'file alternate outline',
      size: 'large',
      actionType: 'download'
    }
  ]

  const {
    isLoading: isBusyData,
    data: ftplogsData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = MilkMoneyQuery.getMilkMoneyFtpLogs({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
    dateFrom: dataFiltered.dateFrom !== '' ? parseDate(dataFiltered.dateFrom) : '',
    dateTo: dataFiltered.dateTo !== '' ? parseDate(dataFiltered.dateTo) : ''
  });

  const {
    data: ftplogsOptions,
    refetch: fetchOptions,
    isLoading: isBusyOptions
  } = MilkMoneyQuery.getMilkMoneyLogsOptions()

  useEffect(() => {
    fetchOptions()
    fetchFilteredData()
  }, [t])

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData();
    }
  }, [dataFiltered]);

  function renderFilters() {
    return <FiltersWrapper>
      <DateInput
        name='date'
        autoComplete='off'
        placeholder={t('Date from')}
        closable
        clearIcon={'close'}
        onKeyPress={handleKeyPress}
        clearable
        localization={languageLocal.code}
        closeOnMouseLeave={false}
        dateFormat={'DD.MM.YYYY'}
        className={'datePicker'}
        value={dataFiltered.dateFrom}
        iconPosition='left'
        onChange={(event, { name, value }) => setDataFiltered({
          ...dataFiltered,
          dateFrom: value
        })}
      />
      <DateInput
        name='date'
        autoComplete='off'
        placeholder={t('Date to')}
        closable
        clearIcon={'close'}
        clearable
        disabled={dataFiltered.dateFrom === ''}
        localization={languageLocal.code}
        onKeyPress={handleKeyPress}
        closeOnMouseLeave={false}
        dateFormat={'DD.MM.YYYY'}
        className={'datePicker'}
        minDate={dataFiltered.dateFrom}
        value={dataFiltered.dateTo || ''}
        initialDate={dataFiltered.dateTo !== '' ? dataFiltered.dateTo : dataFiltered.dateFrom}
        iconPosition='left'
        onChange={(event, { name, value }) => {
          setDataFiltered({
            ...dataFiltered,
            dateTo: value
          })
        }}
      />
      <Dropdown
        selectOnBlur={false}
        selection
        clearable
        options={ftplogsOptions?.types.map(type => ({ value: type.key, text: type.label })) || []}
        value={dataFiltered.type}
        placeholder={t('Select type')}
        //@ts-ignore
        onChange={(e, data) => setDataFiltered({ ...dataFiltered, type: data.value })}
      />
      <Dropdown
        selectOnBlur={false}
        clearable
        selection
        placeholder={t('Select ftp')}
        options={ftplogsOptions?.ftps.map(ftp => ({ value: ftp.key, text: ftp.label })) || []}
        value={dataFiltered.ftp}
        //@ts-ignore
        onChange={(e, data) => setDataFiltered({ ...dataFiltered, ftp: data.value })}
      />
    </FiltersWrapper>
  }

  async function handleDownloadOriginal(originalFiles, id, type) {
    setLoadingAction({
      type,
      id: id,
      value: true,
      idKey: 'id'
    })
    try {
      for (let i = 0; i <= originalFiles.length - 1; i++) {
        if (originalFiles[i]) {
          await DownloadService.downloadFile(`milk-money/ftp-logs/${id}/get-file`, originalFiles[i], { fileName: originalFiles[i] })
        }
      }
    } catch (error) {
      if (error.response?.data) {
        notify(error.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function parseDate(value) {
    const arrayOfValues = value.split('.')
    return `${arrayOfValues[2]}-${arrayOfValues[1]}-${arrayOfValues[0]}`
  }

  async function handleDownloadLogFile(id, type) {
    setLoadingAction({
      type,
      id: id,
      value: true,
      idKey: 'id'
    })
    try {
      await DownloadService.downloadFile(`milk-money/ftp-logs/${id}/get-log-file`, 'log_file')
    } catch (error) {
      if (error.response?.data) {
        notify(error.response?.data?.message, 'error');
      } else {
        notify(t('General error'), 'error');
      }
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'download original':
        return row.originalFiles.length === 0 ? notify(t('No original files'), 'info') : handleDownloadOriginal(row?.originalFiles || [], row?.id, type)
      case 'download':
        return handleDownloadLogFile(row.id, type)
    }
  }

  function renderTable() {
    return <TableComponent
      isFetchingData={isFetchingData}
      animated
      actions={actions}
      loadingAction={loadingAction}
      tableColumnHeaders={getTableColumnHeaders()}
      dataFiltered={dataFiltered}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      handleChangeDataFiltered={(data) => setDataFiltered(data)}
      rows={rows}
      total={filtered}
    />
  }
  const { filtered, rows } = ftplogsData ? ftplogsData : { filtered: 0, rows: [] };

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  function renderActions() {
    const payload = {
      ...dataFiltered,
      dateFrom: dataFiltered.dateFrom !== '' ? parseDate(dataFiltered.dateFrom) : '',
      dateTo: dataFiltered.dateTo !== '' ? parseDate(dataFiltered.dateTo) : ''
    }
    return <ExcelExport
      url={'milk-money/ftp-logs/export'}
      fileName={'ftp_log'}
      data={payload}
    />
  }

  return <Layout
    title={t('FTP logs')}
    isBusy={isBusyData || isBusyOptions}
    segmented
    actions={!lightUserLogged && renderActions()}
    filters={renderFilters()}
  >
    {ftplogsData && ftplogsOptions && renderTable()}
  </Layout>

}
export default FtpLogs